// Chat attachment data: authenticated fetches, the blob cache, downloads,
// and bounded preview payloads (a small one per card, a larger one per viewer).
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';
import { triggerBlobDownload } from '@apps/web/lib/tabular_export';
import {
    type AttachmentPreviewRead,
    type Mode,
} from '@libraries/typescript/api_client';

const ATTACHMENT_URL_TEMPLATE =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/attachments/{attachment_id}' as const;

async function fetchAttachmentResponse({
    organizationId,
    mode,
    runId,
    attachmentId,
    download,
}: {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
    readonly download: boolean;
}) {
    // Run routes can use a different mode from the global mode store.
    // Keep attachment requests in the rendered run's scope and use the
    // API client for authentication.
    return getApiClient().raw<typeof ATTACHMENT_URL_TEMPLATE, 'get'>({
        method: 'GET',
        mode,
        url: ATTACHMENT_URL_TEMPLATE,
        pathParams: {
            organization_id: organizationId,
            phoebe_agent_run_id: runId,
            attachment_id: attachmentId,
        },
        queryParams: download ? { download: true } : {},
        reason: 'v3-chat-attachment',
    });
}

async function fetchAttachmentBlob(args: {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
}): Promise<Blob> {
    const response = await fetchAttachmentResponse({
        ...args,
        download: false,
    });
    if (!response.ok) {
        throw new Error(`Attachment fetch failed with ${response.status}`);
    }
    return response.blob();
}

// Blob-based download: fetch the file through the api client so `X-Mode`
// (and every other auth header the api client injects) rides along, then
// hand the bytes to the shared `triggerBlobDownload` from
// `apps/web/lib/tabular_export` — that helper owns the Arc/Chromium-safe
// dispatch (a real MouseEvent instead of `.click()`) AND the delayed
// `URL.revokeObjectURL` so Chromium/Arc actually get to fetch the blob
// before it's freed. A plain `<a href download>` cannot set headers, so
// navigating to the download URL directly would omit `X-Mode` and the
// endpoint would reject the request with 422 (see
// attachments.py::require_mode_context_from_header).
export function useAttachmentDownload({
    organizationId,
    mode,
    runId,
    attachmentId,
    filename,
}: {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filename: string;
}) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const startDownload = async () => {
        setDownloadError(null);
        setIsDownloading(true);
        try {
            const response = await fetchAttachmentResponse({
                organizationId,
                mode,
                runId,
                attachmentId,
                download: true,
            });
            if (!response.ok) {
                throw new Error(`Download failed with ${response.status}`);
            }
            const blob = await response.blob();
            triggerBlobDownload(blob, filename);
        } catch (error) {
            setDownloadError(
                error instanceof Error ? error.message : 'Download failed',
            );
        } finally {
            setIsDownloading(false);
        }
    };
    return { isDownloading, downloadError, startDownload };
}

// Bounded LRU for viewed attachment bytes. The global SWR cache never
// evicts, so caching blobs there would retain every viewed file for the life
// of the tab (up to 10 MB apiece at the attachment cap). This keeps recent
// scroll-back through virtualized rows instant while older files are
// refetched on revisit instead of held forever.
const BLOB_CACHE_MAX_BYTES = 32 * 1024 * 1024;
const blobCache = new Map<string, Blob>();
let blobCacheBytes = 0;

function readCachedBlob(cacheKey: string): Blob | undefined {
    const blob = blobCache.get(cacheKey);
    if (blob !== undefined) {
        // Re-insert so Map iteration order doubles as recency order.
        blobCache.delete(cacheKey);
        blobCache.set(cacheKey, blob);
    }
    return blob;
}

function writeCachedBlob(cacheKey: string, blob: Blob): void {
    if (blob.size > BLOB_CACHE_MAX_BYTES) {
        return;
    }
    const existing = blobCache.get(cacheKey);
    if (existing !== undefined) {
        blobCache.delete(cacheKey);
        blobCacheBytes -= existing.size;
    }
    blobCache.set(cacheKey, blob);
    blobCacheBytes += blob.size;
    for (const [oldestKey, oldestBlob] of blobCache) {
        if (blobCacheBytes <= BLOB_CACHE_MAX_BYTES || oldestKey === cacheKey) {
            break;
        }
        blobCache.delete(oldestKey);
        blobCacheBytes -= oldestBlob.size;
    }
}

export function useAttachmentBlob({
    organizationId,
    mode,
    runId,
    attachmentId,
}: {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
}): { readonly blob: Blob | null; readonly failed: boolean } {
    const cacheKey = [organizationId, mode, runId, attachmentId].join(' ');
    const [state, setState] = useState<{
        readonly key: string;
        readonly blob: Blob | null;
        readonly failed: boolean;
    }>({ key: cacheKey, blob: null, failed: false });
    useEffect(() => {
        const cached = readCachedBlob(cacheKey);
        if (cached !== undefined) {
            setState({ key: cacheKey, blob: cached, failed: false });
            return;
        }
        let cancelled = false;
        setState({ key: cacheKey, blob: null, failed: false });
        fetchAttachmentBlob({ organizationId, mode, runId, attachmentId }).then(
            (blob) => {
                if (!cancelled) {
                    writeCachedBlob(cacheKey, blob);
                    setState({ key: cacheKey, blob, failed: false });
                }
            },
            () => {
                if (!cancelled) {
                    setState({ key: cacheKey, blob: null, failed: true });
                }
            },
        );
        return () => {
            cancelled = true;
        };
    }, [cacheKey, organizationId, mode, runId, attachmentId]);
    // Ignore state written for a previous identity while the effect for the
    // new one is still pending.
    return state.key === cacheKey
        ? { blob: state.blob, failed: state.failed }
        : { blob: null, failed: false };
}

// Virtualized rows unmount. Cache bytes, then let each mounted preview own
// its URL so revisiting a row never reuses a revoked URL.
export function useBlobObjectUrl(blob: Blob | null): string | undefined {
    const [blobUrl, setBlobUrl] = useState<string>();
    useEffect(() => {
        if (blob === null) {
            setBlobUrl(undefined);
            return;
        }
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [blob]);
    return blobUrl;
}

const PREVIEW_URL_TEMPLATE =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/attachments/{attachment_id}/preview' as const;

export type AttachmentPreviewRequest = {
    readonly organizationId: string;
    readonly mode: Mode;
    readonly runId: string;
    readonly attachmentId: string;
    /** True for the viewer’s larger budget; cards read a prefix. */
    readonly full: boolean;
};

export function useAttachmentPreview({
    organizationId,
    mode,
    runId,
    attachmentId,
    full,
}: AttachmentPreviewRequest): {
    readonly preview: AttachmentPreviewRead | null;
    readonly failed: boolean;
} {
    const { data, error } = useSWR<AttachmentPreviewRead>(
        [
            'v3-chat-attachment-preview',
            organizationId,
            mode,
            runId,
            attachmentId,
            full,
        ],
        () =>
            getApiClient().get({
                url: PREVIEW_URL_TEMPLATE,
                mode,
                pathParams: {
                    organization_id: organizationId,
                    phoebe_agent_run_id: runId,
                    attachment_id: attachmentId,
                },
                queryParams: full ? { full: true } : {},
                reason: 'v3-chat-attachment-preview',
            }),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    );
    return { preview: data ?? null, failed: error !== undefined };
}
